import React from 'react';
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { FaInstagram, FaLinkedinIn } from 'react-icons/fa';
import Lottie from 'lottie-react';
import OurTeam from './OurTeam';
import '../csscomponent/About.css';
import founderImage from '../assest/Saipan.png';
import aboutus from '../Company.json';
import { publicAsset } from '../lib/publicPath';
import { getCmsSettings } from '../lib/sqlAuth';
import { subscribeCmsUpdates } from '../lib/cmsSync';

function About() {
  const defaultInstagramUrl = 'https://www.instagram.com/saipan_naikwadi';
  const defaultLinkedinUrl = 'https://www.linkedin.com/in/saipannaikwadi';
  const [content, setContent] = useState({
    heroTitle: 'Helping businesses succeed through practical development.',
    heroDescription: 'We partner with startups and growing teams to craft websites and digital products that are fast, useful, and easy to scale.',
    founderName: 'Saipan Naikwadi',
    founderRole: 'Founder & CEO',
    founderImage: '/images/Saipan.png',
    founderIntro: 'I am Saipan Naikwadi, Founder and CEO of Services and Developer.',
    founderBio: 'I focus on building modern, practical digital products and websites that help businesses grow with clarity, speed, and quality execution.',
    founderBio2: 'Since starting this company in 2025, my goal has been simple: deliver reliable digital solutions that create real business impact.',
    instagramUrl: defaultInstagramUrl,
    linkedinUrl: defaultLinkedinUrl,
    blocks: [
      {
        title: 'Our Mission',
        text: 'We turn strong business ideas into reliable digital products by combining practical design with robust engineering.',
        image: '/images/missionn.jpeg',
      },
      {
        title: 'Our Vision',
        text: 'Build a future where quality software is accessible, measurable, and genuinely useful for everyday business.',
        image: '/images/vivion.png',
      },
      {
        title: 'Our Values',
        text: 'Ownership, clarity, and speed. We do fewer things, but we do them with discipline and consistency.',
        image: '/images/vision.jpeg',
      },
    ],
    companyInfo: [
      { label: 'Founded', value: '2025' },
      { label: 'Founder', value: 'Saipan Naikwadi' },
      { label: 'Core Services', value: 'Web Design, Development, CMS, Growth Optimization' },
      { label: 'Product Focus', value: 'Business websites, admin dashboards, and scalable digital products' },
      { label: 'Delivery Style', value: 'Practical, fast, and quality-first execution' },
      { label: 'Support Model', value: 'Transparent milestones, direct communication, and launch support' },
    ],
    companyOverviewTitle: 'Services and Developer since 2025',
    companyOverviewText: 'Started in 2025, Services and Developer was built with one clear mission: to help startups, founders, and growing businesses launch digital products and websites that are not only visually modern, but also practical, fast, and growth-ready. From brand-first landing pages to complete business websites and product experiences, we focus on building solutions that improve customer trust, increase conversion opportunities, and support long-term scalability.',
  });

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await getCmsSettings({ keys: ['about_content'] });
        if (!mounted) return;
        const next = res?.settings?.about_content || {};
        setContent((prev) => ({ ...prev, ...next }));
      } catch (_) {}
    };
    load();
    const unsubscribe = subscribeCmsUpdates((payload) => {
      if (!payload?.scope || ['site', 'about', 'team'].includes(payload.scope)) {
        load();
      }
    });
    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const resolvedFounderImage = !content.founderImage
    || content.founderImage === '/images/logo.png'
    || content.founderImage === 'images/logo.png'
      ? founderImage
      : (content.founderImage.startsWith('http') || content.founderImage.startsWith('/uploads/')
          ? content.founderImage
          : publicAsset(content.founderImage));

  return (
    <div className="about-page">
      <section className="about-hero">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <p>About Us</p>
          <h1>{content.heroTitle}</h1>
          <span>{content.heroDescription}</span>
        </motion.div>
        <img src={publicAsset('images/logo.png')} alt="About us" />
      </section>

      <motion.section
        className="founder-section"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="founder-card">
          <img
            src={resolvedFounderImage}
            alt={content.founderName}
            className="founder-image"
            onError={(event) => {
              event.currentTarget.onerror = null;
              event.currentTarget.src = founderImage;
            }}
          />
          <div className="founder-content">
            <p>Founder Spotlight</p>
            <h2>{content.founderName}</h2>
            <h3>{content.founderRole}</h3>
            <div className="founder-copy">
              <span>{content.founderIntro} {content.founderBio}</span>
              <span>{content.founderBio2}</span>
            </div>
            <div className="founder-socials">
              <a
                href={content.instagramUrl || defaultInstagramUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="Saipan Naikwadi Instagram"
              >
                <FaInstagram />
                <span>Instagram</span>
              </a>
              <a
                href={content.linkedinUrl || defaultLinkedinUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="Saipan Naikwadi LinkedIn"
              >
                <FaLinkedinIn />
                <span>LinkedIn</span>
              </a>
            </div>
          </div>
        </div>
      </motion.section>

      <section className="about-blocks">
        {(Array.isArray(content.blocks) ? content.blocks : []).map((block, index) => (
          <motion.article
            key={block.title}
            className="about-block"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: index * 0.1 }}
          >
            <img src={block.image ? (block.image.startsWith('http') || block.image.startsWith('/uploads/') ? block.image : publicAsset(block.image)) : publicAsset('images/missionn.jpeg')} alt={block.title} />
            <h3>{block.title}</h3>
            <p>{block.text}</p>
          </motion.article>
        ))}
      </section>

      <motion.section
        className="about-company-info"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <div className="company-info-head">
          <div>
            <p>Company Overview</p>
            <h2>{content.companyOverviewTitle}</h2>
            <div className="company-info-copy">
              <span>{content.companyOverviewText}</span>
            </div>
          </div>
          <div className="about-company-lottie">
            <Lottie animationData={aboutus} loop />
          </div>
        </div>

        <div className="company-info-grid">
          {(Array.isArray(content.companyInfo) ? content.companyInfo : []).map((item) => (
            <article key={item.label} className="company-info-card">
              <h3>{item.label}</h3>
              <p>{item.value}</p>
            </article>
          ))}
        </div>
      </motion.section>

      <OurTeam />
    </div>
  );
}

export default About;
